import { Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import {
  loadConnectAndInitialize,
  type StripeConnectInstance,
} from "@stripe/connect-js";
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
} from "@stripe/react-connect-js";
import { useEffect, useRef, useState } from "react";

import { stripeConnectFonts, stripeKycAppearance } from "~/theme";

interface KycOnboardingProps {
  clientId: string;
  publishableKey: string;
  onExit: () => void;
}

export function KycOnboarding({
  clientId,
  publishableKey,
  onExit,
}: KycOnboardingProps) {
  const [connectInstance, setConnectInstance] =
    useState<StripeConnectInstance | null>(null);

  const initializedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const key = `${clientId}:${publishableKey}`;
    if (initializedKeyRef.current === key) {
      return;
    }
    initializedKeyRef.current = key;

    setConnectInstance(
      loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () =>
          (await Query.Client().core_getClientOnboardingSession(clientId))
            .client_secret,
        fonts: stripeConnectFonts,
        appearance: stripeKycAppearance,
      }),
    );
  }, [publishableKey, clientId]);

  if (connectInstance === null) {
    return null;
  }

  return (
    <Box data-testid="kyc-onboarding-panel">
      <ConnectComponentsProvider connectInstance={connectInstance}>
        <ConnectAccountOnboarding onExit={onExit} />
      </ConnectComponentsProvider>
    </Box>
  );
}
